import { useState, useEffect, useCallback, type ReactNode } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Box,
  Checkbox,
  useColorModeValue,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
} from '@chakra-ui/react';
import { WarningIcon } from '@chakra-ui/icons';

import { CONSENT_METHOD_VERBAL, CONSENT_METHOD_WRITTEN } from '../constants';
import { COVERAGE_DECLINE_CONFIG } from '../constants/coverageDeclineConfig';

interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (consentMethod: number) => void;
  isLoading?: boolean;
}

interface ConfigBasedProps extends BaseModalProps {
  consentType: number;
  title?: never;
  warningContent?: never;
  attestationText?: never;
}

interface CustomContentProps extends BaseModalProps {
  consentType?: never;
  title: string;
  warningContent: ReactNode;
  attestationText: string;
}

export type CoverageConsentModalProps = ConfigBasedProps | CustomContentProps;

const CoverageConsentModal = (props: CoverageConsentModalProps) => {
  const { isOpen, onClose, onConfirm, isLoading = false } = props;

  const config =
    'consentType' in props && props.consentType
      ? COVERAGE_DECLINE_CONFIG[props.consentType]
      : null;

  const title = config?.title ?? ('title' in props ? props.title : '');
  const attestationText =
    config?.attestationText ??
    ('attestationText' in props ? props.attestationText : '');

  const warningBg = useColorModeValue('orange.50', 'orange.900');
  const warningBorder = useColorModeValue('orange.200', 'orange.700');

  const warningContent =
    'warningContent' in props && props.warningContent ? (
      props.warningContent
    ) : config ? (
      <>
        <Text
          fontSize="sm"
          fontWeight="600"
          mb={config.warningDescription ? 2 : 0}
        >
          {config.warningTitle}
        </Text>
        {config.warningDescription && (
          <Text fontSize="sm">{config.warningDescription}</Text>
        )}
      </>
    ) : null;

  const [isChecked, setIsChecked] = useState(false);
  const [consentMethod, setConsentMethod] = useState<string>(
    String(CONSENT_METHOD_VERBAL),
  );

  useEffect(() => {
    if (isOpen) {
      setIsChecked(false);
      setConsentMethod(String(CONSENT_METHOD_VERBAL));
    }
  }, [isOpen]);

  const handleConfirm = useCallback(() => {
    onConfirm(parseInt(consentMethod, 10));
  }, [onConfirm, consentMethod]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} isCentered size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Box display="flex" alignItems="center" gap={2}>
            <WarningIcon color="orange.500" />
            <Text>{title}</Text>
          </Box>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Box
              p={4}
              bg={warningBg}
              borderRadius="md"
              borderWidth="1px"
              borderColor={warningBorder}
            >
              {warningContent}
            </Box>

            <Text fontSize="sm" color="gray.600" _dark={{ color: 'gray.400' }}>
              Please confirm the following:
            </Text>

            <Checkbox
              isChecked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              colorScheme="blue"
              alignItems="flex-start"
            >
              <Text fontSize="sm" mt="-2px">
                {attestationText}
              </Text>
            </Checkbox>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600">
                Consent Method
              </FormLabel>
              <RadioGroup value={consentMethod} onChange={setConsentMethod}>
                <VStack align="start" spacing={2}>
                  <Radio
                    value={String(CONSENT_METHOD_VERBAL)}
                    colorScheme="blue"
                  >
                    Verbal Consent
                  </Radio>
                  <Radio
                    value={String(CONSENT_METHOD_WRITTEN)}
                    colorScheme="blue"
                  >
                    Written Consent
                  </Radio>
                </VStack>
              </RadioGroup>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={handleCancel} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="brand"
            onClick={handleConfirm}
            isDisabled={!isChecked}
            isLoading={isLoading}
          >
            Confirm
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CoverageConsentModal;
